import React from 'react';
import {
  Clock,
  ShieldAlert,
  Layers,
  Zap,
  ShieldCheck,
  Globe2,
  CheckCircle2,
  ArrowRight,
  Compass
} from 'lucide-react';

interface WhyUibnSectionProps {
  onOpenRegister?: () => void;
  onExploreCatalog?: () => void;
}

export const WhyUibnSection: React.FC<WhyUibnSectionProps> = ({
  onOpenRegister,
  onExploreCatalog,
}) => {
  const comparisons = [
    {
      problemTitle: 'Birokrasi Lambat & Kuota Terbatas',
      problemDesc:
        'Proses permohonan penomoran buku konvensional sering memakan waktu, antrean verifikasi manual yang panjang, serta pembatasan kuota yang menyulitkan penulis mandiri dan penerbit berkembang.',
      problemIcon: Clock,
      solutionTitle: 'Penerbitan Nomor Instan Real-Time',
      solutionDesc:
        'UIBN mengotomatisasi penomoran dengan algoritma checksum modulo-10 yang menghasilkan kode buku resmi, barcode, dan QR code seketika tanpa antrean dan tanpa batasan kuota berkala.',
      solutionIcon: Zap,
    },
    {
      problemTitle: 'Maraknya Pembajakan & Klaim Ilegal',
      problemDesc:
        'Buku fisik rentan dicetak ulang tanpa izin oleh oknum pembajak, sementara masyarakat dan toko buku kesulitan membedakan karya asli dengan cetakan ilegal secara langsung di lapangan.',
      problemIcon: ShieldAlert,
      solutionTitle: 'Verifikasi Otentikasi Digital 1-Detik',
      solutionDesc:
        'Setiap buku ber-UIBN memiliki QR code unik dan barcode terstandarisasi yang langsung mengarah ke halaman verifikasi resmi, memungkinkan pembaca dan distributor menguji keaslian lewat kamera ponsel.',
      solutionIcon: ShieldCheck,
    },
    {
      problemTitle: 'Katalog Terfragmentasi & Tertutup',
      problemDesc:
        'Data kepenulisan dan katalog terbitan sering tersimpan di basis data terpisah yang sulit diakses oleh publik, pegiat literasi, perpustakaan daerah, maupun aplikasi toko buku modern.',
      problemIcon: Layers,
      solutionTitle: 'Ekosistem Terbuka & API Terpadu',
      solutionDesc:
        'UIBN menyediakan katalog buku nasional yang transparan dan REST API modern, mempermudah integrasi data untuk perpustakaan, marketplace, institusi riset, dan pengarsipan digital.',
      solutionIcon: Globe2,
    },
  ];

  return (
    <section id="section-why-uibn" className="w-full max-w-7xl 2xl:max-w-[1536px] 3xl:max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-10 space-y-10 2xl:space-y-12">
      {/* Header */}
      <div className="text-center max-w-3xl 2xl:max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs sm:text-sm 2xl:text-base font-semibold shadow-2xs">
          <Compass className="w-3.5 h-3.5 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5 text-indigo-600" />
          <span>Latar Belakang & Visi Nasional</span>
        </div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl 2xl:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
          Mengapa UIBN Hadir di Indonesia?
        </h2>
        <p className="text-sm sm:text-base md:text-lg 2xl:text-xl text-slate-600 leading-relaxed">
          Menjawab kesenjangan antara pesatnya produksi literasi modern dengan lambatnya sistem identifikasi buku konvensional. UIBN hadir memberikan kepastian hukum, kecepatan teknologi, dan kemudahan akses bagi seluruh pegiat literasi.
        </p>
      </div>

      {/* Comparison Cards: Problems vs Solutions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 2xl:gap-8">
        {comparisons.map((item, idx) => {
          const ProblemIcon = item.problemIcon;
          const SolutionIcon = item.solutionIcon;

          return (
            <div
              key={`why-comp-${item.problemTitle}-${idx}`}
              className="bg-white rounded-3xl border border-slate-200/90 shadow-2xs overflow-hidden flex flex-col justify-between hover:border-indigo-200 hover:shadow-sm transition group"
            >
              {/* Problem Part (Top) */}
              <div className="p-6 2xl:p-7 bg-slate-50/70 border-b border-slate-100 space-y-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 2xl:w-9 2xl:h-9 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                    <ProblemIcon className="w-4 h-4 2xl:w-5 2xl:h-5" />
                  </div>
                  <span className="text-[11px] sm:text-xs 2xl:text-sm font-bold text-rose-700 uppercase tracking-wider">
                    Tantangan Lama
                  </span>
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl 2xl:text-2xl font-bold text-slate-900">
                  {item.problemTitle}
                </h3>
                <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-600 leading-relaxed">
                  {item.problemDesc}
                </p>
              </div>

              {/* Solution Part (Bottom) */}
              <div className="p-6 2xl:p-7 space-y-2.5 flex-1 flex flex-col justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 2xl:w-9 2xl:h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                      <SolutionIcon className="w-4 h-4 2xl:w-5 2xl:h-5" />
                    </div>
                    <span className="text-[11px] sm:text-xs 2xl:text-sm font-bold text-emerald-700 uppercase tracking-wider">
                      Solusi UIBN
                    </span>
                  </div>
                  <h4 className="text-base sm:text-lg lg:text-xl 2xl:text-2xl font-bold text-slate-900">
                    {item.solutionTitle}
                  </h4>
                  <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-600 leading-relaxed">
                    {item.solutionDesc}
                  </p>
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center gap-1.5 text-xs sm:text-sm 2xl:text-base font-semibold text-emerald-600">
                  <CheckCircle2 className="w-4 h-4 2xl:w-5 2xl:h-5" />
                  <span>Terstandarisasi & Otomatis</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quote / Mission Banner */}
      <div className="p-6 sm:p-8 2xl:p-10 rounded-3xl bg-slate-900 text-white shadow-lg relative overflow-hidden border border-slate-800">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6 2xl:gap-8">
          <div className="space-y-2 max-w-2xl 2xl:max-w-3xl">
            <span className="text-[11px] sm:text-xs 2xl:text-sm font-bold text-indigo-400 uppercase tracking-wider block">
              Misi Digitalisasi Perbukuan
            </span>
            <h3 className="text-lg sm:text-xl 2xl:text-2xl font-bold text-white tracking-tight leading-snug">
              &quot;Setiap karya tulis berharga layak memiliki identitas resmi yang cepat, transparan, dan terlindungi.&quot;
            </h3>
            <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-300 leading-relaxed">
              UIBN membuka pintu seluas-luasnya bagi penulis indie, akademisi, penerbit, dan komunitas literasi nusantara untuk berkarya tanpa terbelenggu antrean birokrasi.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
            {onOpenRegister && (
              <button
                type="button"
                id="btn-why-uibn-register"
                onClick={onOpenRegister}
                className="px-5 sm:px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs sm:text-sm 2xl:text-base transition flex items-center justify-center gap-2 shadow-xs cursor-pointer"
              >
                <span>Daftar Akun Baru</span>
                <ArrowRight className="w-4 h-4 2xl:w-5 2xl:h-5" />
              </button>
            )}
            {onExploreCatalog && (
              <button
                type="button"
                id="btn-why-uibn-explore"
                onClick={onExploreCatalog}
                className="px-5 sm:px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-xs sm:text-sm 2xl:text-base border border-white/10 transition flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Lihat Katalog Terdaftar</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
